<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { messages } from "@/i18n";

import Footer from "@/components/Footer.vue";
import Navbar from "@/components/Navbar.vue";

// ===== lang handling =====
const normalizeLang = (raw) =>
  String(raw || "").toLowerCase().startsWith("en") ? "en" : "ar";

const lang = ref(
  normalizeLang(
    document.documentElement.lang || localStorage.getItem("app_lang") || "ar"
  )
);

const isAr = computed(() => lang.value === "ar");
let langObserver = null;

// ===== translator =====
const getMessage = (key) => {
  const base = messages?.[lang.value];
  if (!base) return null;

  if (base[key] != null) return base[key];
  if (!key.includes(".")) return null;

  return key.split(".").reduce((acc, part) => acc?.[part], base) ?? null;
};

const tr = (key, vars = null) => {
  const val = getMessage(key);
  if (val == null) return key;

  if (vars && typeof val === "string") {
    return val.replace(/\{(\w+)\}/g, (_, k) => (vars?.[k] ?? `{${k}}`));
  }
  return val;
};

// ===== sections =====
const sections = computed(() => [
  {
    id: "overview",
    icon: "mdi mdi-shield-lock-outline",
    title: tr("legal.privacy.sections.intro.title"),
    body: tr("legal.privacy.sections.intro.body"),
    tone: "teal",
  },
  {
    id: "collect",
    icon: "mdi mdi-database-outline",
    title: tr("legal.privacy.sections.data_we_collect.title"),
    bullets: [
      tr("legal.privacy.sections.data_we_collect.b1"),
      tr("legal.privacy.sections.data_we_collect.b2"),
      tr("legal.privacy.sections.data_we_collect.b3"),
      tr("legal.privacy.sections.data_we_collect.b4"),
    ],
    tone: "primary",
  },
  {
    id: "use",
    icon: "mdi mdi-target-account",
    title: tr("legal.privacy.sections.how_we_use.title"),
    bullets: [
      tr("legal.privacy.sections.how_we_use.b1"),
      tr("legal.privacy.sections.how_we_use.b2"),
      tr("legal.privacy.sections.how_we_use.b3"),
      tr("legal.privacy.sections.how_we_use.b4"),
    ],
    tone: "gold",
  },
  {
    id: "sharing",
    icon: "mdi mdi-share-variant-outline",
    title: tr("legal.privacy.sections.sharing.title"),
    bullets: [
      tr("legal.privacy.sections.sharing.b1"),
      tr("legal.privacy.sections.sharing.b2"),
      tr("legal.privacy.sections.sharing.b3"),
    ],
    tone: "teal",
  },
  {
    id: "cookies",
    icon: "mdi mdi-cookie-outline",
    title: tr("legal.privacy.sections.cookies.title"),
    body: tr("legal.privacy.sections.cookies.body"),
    tone: "primary",
  },
  {
    id: "rights",
    icon: "mdi mdi-account-lock-outline",
    title: tr("legal.privacy.sections.rights.title"),
    bullets: [
      tr("legal.privacy.sections.rights.b1"),
      tr("legal.privacy.sections.rights.b2"),
      tr("legal.privacy.sections.rights.b3"),
      tr("legal.privacy.sections.rights.b4"),
    ],
    tone: "gold",
  },
  {
    id: "retention",
    icon: "mdi mdi-timer-sand",
    title: tr("legal.privacy.sections.retention.title"),
    body: tr("legal.privacy.sections.retention.body"),
    tone: "primary",
  },
  {
    id: "contact",
    icon: "mdi mdi-email-outline",
    title: tr("legal.privacy.sections.contact.title"),
    body: tr("legal.privacy.sections.contact.body"),
    tone: "teal",
  },
]);

const lastUpdated = computed(() =>
  tr("legal.last_updated", { date: "2026-01-01" })
);

// ===== tone (identity) =====
function toneClass(tone) {
  switch (tone) {
    case "teal":
      return "tone tone--teal";
    case "gold":
      return "tone tone--gold";
    case "primary":
    default:
      return "tone tone--primary";
  }
}

// ===== navigation / active section =====
const activeId = ref("overview");
const tocOpen = ref(false);

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  tocOpen.value = false;
}

let sectionObserver = null;

onMounted(() => {
  // watch html lang
  langObserver = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.attributeName === "lang") {
        lang.value = normalizeLang(document.documentElement.lang || "ar");
      }
    }
  });
  langObserver.observe(document.documentElement, { attributes: true });

  // active section highlight
  sectionObserver = new IntersectionObserver(
    (entries) => {
      // pick the most visible intersecting entry
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0))[0];
      if (visible?.target?.id) activeId.value = visible.target.id;
    },
    {
      root: null,
      rootMargin: "-20% 0px -70% 0px",
      threshold: [0.05, 0.15, 0.25, 0.35, 0.45, 0.6],
    }
  );

  // observe after paint
  requestAnimationFrame(() => {
    sections.value.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) sectionObserver.observe(el);
    });
  });
});

onBeforeUnmount(() => {
  if (langObserver) langObserver.disconnect();
  if (sectionObserver) sectionObserver.disconnect();
});
</script>

<template>
  <Navbar />

  <section
    :dir="isAr ? 'rtl' : 'ltr'"
    class="legalWrap relative w-full overflow-hidden py-10 md:py-16 dark:bg-slate-900"
  >
    <!-- background -->
    <div class="legalBg" aria-hidden="true"></div>

    <div class="container mx-auto max-w-6xl px-4">
      <!-- Top bar -->
      <div class="legalTop">
        <div class="legalCrumb">
          <span class="legalCrumb__home">
            <i class="mdi mdi-home-outline" aria-hidden="true"></i>
            <span>{{ tr("home") }}</span>
          </span>
          <span class="legalCrumb__sep">/</span>
          <span class="legalCrumb__here">{{ tr("legal.privacy.title") }}</span>
        </div>

        <div class="legalTop__right">
          <span class="legalPill">
            <i class="mdi mdi-calendar-blank-outline" aria-hidden="true"></i>
            <span>{{ lastUpdated }}</span>
          </span>

          <a class="btn btn--ghost" href="#contact">
            <i class="mdi mdi-chat-outline" aria-hidden="true"></i>
            <span>{{ tr("contact_us") ?? tr("legal.privacy.sections.contact.title") }}</span>
          </a>
        </div>
      </div>

      <!-- Hero -->
      <header class="hero">
        <div class="hero__left">
          <div class="hero__badge">
            <i class="mdi mdi-shield-check-outline" aria-hidden="true"></i>
            <span>{{ tr("legal.privacy.badge") }}</span>
          </div>

          <h1 class="hero__title">{{ tr("legal.privacy.title") }}</h1>
          <p class="hero__subtitle">{{ tr("legal.privacy.subtitle") }}</p>

          <div class="hero__chips">
            <span class="chip">
              <i class="mdi mdi-lock-outline" aria-hidden="true"></i>
              <span>{{ tr("legal.read_time", { min: 5 }) }}</span>
            </span>

            <span class="chip chip--soft">
              <i class="mdi mdi-check-decagram-outline" aria-hidden="true"></i>
              <span>{{ tr("legal.consent.privacy") }}</span>
            </span>
          </div>
        </div>

        <div class="hero__right" aria-hidden="true">
          <div class="heroCard">
            <div class="heroCard__icon">
              <i class="mdi mdi-account-lock-outline" aria-hidden="true"></i>
            </div>
            <div class="heroCard__txt">
              <div class="heroCard__title">{{ tr("legal.privacy.sections.rights.title") }}</div>
              <div class="heroCard__sub">{{ tr("legal.privacy.sections.rights.b2") }}</div>
            </div>
          </div>

          <div class="heroTag">
            <i class="mdi mdi-cookie-outline" aria-hidden="true"></i>
            <span>{{ tr("legal.privacy.sections.cookies.title") }}</span>
          </div>
        </div>
      </header>

      <!-- Mobile TOC -->
      <div class="tocMobile">
        <button class="tocMobile__btn" type="button" @click="tocOpen = !tocOpen">
          <span class="tocMobile__left">
            <span class="tocMobile__dot"></span>
            <span class="tocMobile__label">
              {{ tr("legal.on_this_page") ?? (isAr ? "في هذه الصفحة" : "On this page") }}
            </span>
          </span>

          <span class="tocMobile__active">
            <span class="truncate">
              {{ sections.find((s) => s.id === activeId)?.title ?? "" }}
            </span>
            <i class="mdi" :class="tocOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"></i>
          </span>
        </button>

        <div v-show="tocOpen" class="tocMobile__panel">
          <button
            v-for="s in sections"
            :key="s.id"
            type="button"
            class="tocItem"
            :class="{ 'tocItem--active': activeId === s.id }"
            @click="scrollToSection(s.id)"
          >
            <i class="mdi mdi-circle-small" aria-hidden="true"></i>
            <span class="truncate">{{ s.title }}</span>
          </button>
        </div>
      </div>

      <!-- Layout -->
      <div class="layout">
        <!-- Desktop TOC -->
        <aside class="side">
          <div class="sideCard">
            <div class="sideCard__head">
              <i class="mdi mdi-format-list-bulleted" aria-hidden="true"></i>
              <span>{{ tr("legal.on_this_page") ?? (isAr ? "في هذه الصفحة" : "On this page") }}</span>
            </div>

            <div class="sideCard__list">
              <button
                v-for="s in sections"
                :key="s.id"
                type="button"
                class="sideLink"
                :class="{ 'sideLink--active': activeId === s.id }"
                @click="scrollToSection(s.id)"
              >
                <span class="sideLink__dot"></span>
                <span class="truncate">{{ s.title }}</span>
              </button>
            </div>
          </div>
        </aside>

        <!-- Main -->
        <main class="main">
          <article
            v-for="s in sections"
            :key="s.id"
            :id="s.id"
            class="card"
            :class="{ 'card--active': activeId === s.id }"
          >
            <div class="card__head">
              <div class="iconBox" :class="toneClass(s.tone)">
                <i :class="s.icon" aria-hidden="true"></i>
              </div>

              <div class="min-w-0">
                <h2 class="card__title">{{ s.title }}</h2>
                <p v-if="s.id === 'overview'" class="card__hint">
                  {{ tr("legal.privacy.badge") }} • {{ tr("legal.read_time", { min: 5 }) }}
                </p>
              </div>
            </div>

            <p v-if="s.body" class="card__text">{{ s.body }}</p>

            <ul v-if="s.bullets" class="card__list">
              <li v-for="(b, i) in s.bullets" :key="i">{{ b }}</li>
            </ul>

            <div class="card__footer">
              <button class="miniBtn" type="button" @click="scrollToSection('contact')">
                <i class="mdi mdi-email-fast-outline" aria-hidden="true"></i>
                <span>{{ tr("legal.privacy.sections.contact.title") }}</span>
              </button>

              <button class="miniBtn miniBtn--ghost" type="button" @click="scrollToSection('overview')">
                <i class="mdi mdi-arrow-up" aria-hidden="true"></i>
                <span>{{ isAr ? "العودة للأعلى" : "Back to top" }}</span>
              </button>
            </div>
          </article>
        </main>
      </div>

      <!-- Note / CTA -->
      <div class="note">
        <div class="note__left">
          <div class="note__icon">
            <i class="mdi mdi-information-outline" aria-hidden="true"></i>
          </div>
          <div class="note__txt">
            <div class="note__title">{{ isAr ? "ملاحظة" : "Note" }}</div>
            <div class="note__desc">{{ tr("legal.consent.privacy") }}</div>
          </div>
        </div>

        <a class="btn" href="#contact">
          <i class="mdi mdi-email-fast-outline" aria-hidden="true"></i>
          <span>{{ tr("legal.privacy.sections.contact.title") }}</span>
        </a>
      </div>
    </div>
  </section>

  <Footer />
</template>

<style scoped>
/* ===== Identity tokens (soft, not “too yellow”) ===== */
.legalWrap {
  --bg: #f7fbfb;
  --surface: rgba(255, 255, 255, 0.82);
  --surface2: rgba(255, 255, 255, 0.9);

  --text: #0f172a;
  --muted: #64748b;

  --border: rgba(148, 163, 184, 0.28);
  --shadow: 0 18px 45px rgba(15, 23, 42, 0.07);

  --brand: #0f7f74;
  --brandSoft: rgba(15, 127, 116, 0.10);

  /* very soft gold accent */
  --gold: #c9a23a;
  --goldSoft: rgba(201, 162, 58, 0.11);

  /* primary light */
  --primarySoft: rgba(205, 239, 251, 0.55);
  background: var(--bg);
}

/* Background: calmer, cleaner */
.legalBg {
  position: absolute;
  inset: -80px;
  background:
    radial-gradient(circle at 12% 18%, rgba(205, 239, 251, 0.55), transparent 55%),
    radial-gradient(circle at 84% 28%, rgba(230, 246, 244, 0.70), transparent 58%),
    radial-gradient(circle at 35% 88%, rgba(201, 162, 58, 0.10), transparent 58%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.92), rgba(248, 250, 252, 0.70));
  opacity: 0.95;
}

/* ===== Top row ===== */
.legalTop {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.legalCrumb {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--muted);
  font-weight: 900;
  font-size: 12px;
}
.legalCrumb__home {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.legalCrumb__sep {
  opacity: 0.6;
}
.legalCrumb__here {
  color: var(--text);
  font-weight: 950;
}

.legalTop__right {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.legalPill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  background: var(--surface2);
  border: 1px solid var(--border);
  color: var(--text);
  font-weight: 950;
  font-size: 12px;
}

/* ===== Buttons ===== */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 14px;
  border-radius: 14px;
  background: var(--brand);
  color: #fff;
  font-weight: 950;
  font-size: 12px;
  border: 1px solid rgba(15, 127, 116, 0.22);
  text-decoration: none;
  transition: 0.16s ease;
}
.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 30px rgba(15, 127, 116, 0.16);
}
.btn--ghost {
  background: var(--surface2);
  color: var(--text);
  border: 1px solid var(--border);
}
.btn--ghost:hover {
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
}

/* ===== Hero ===== */
.hero {
  position: relative;
  z-index: 1;
  border: 1px solid var(--border);
  background: var(--surface);
  backdrop-filter: blur(8px);
  border-radius: 24px;
  box-shadow: var(--shadow);
  overflow: hidden;

  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: 0;
  margin-bottom: 12px;
}
@media (max-width: 900px) {
  .hero {
    grid-template-columns: 1fr;
  }
}

.hero__left {
  padding: 18px;
}

.hero__badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(230, 246, 244, 0.85);
  color: var(--brand);
  border: 1px solid rgba(15, 127, 116, 0.18);
  font-weight: 950;
  font-size: 12px;
}

.hero__title {
  margin: 10px 0 6px;
  font-size: 26px;
  font-weight: 950;
  color: var(--text);
  letter-spacing: -0.2px;
}
@media (max-width: 640px) {
  .hero__title {
    font-size: 20px;
  }
}

.hero__subtitle {
  margin: 0;
  color: var(--muted);
  font-weight: 800;
  font-size: 13px;
  line-height: 1.85;
  max-width: 64ch;
}

.hero__chips {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(205, 239, 251, 0.45);
  border: 1px solid rgba(15, 23, 42, 0.06);
  color: var(--text);
  font-weight: 900;
  font-size: 12px;
}
.chip--soft {
  background: var(--surface2);
  border-color: var(--border);
  color: var(--muted);
}

.hero__right {
  padding: 18px;
  border-left: 1px solid var(--border);
  background:
    radial-gradient(circle at 25% 25%, rgba(205, 239, 251, 0.45), transparent 60%),
    radial-gradient(circle at 75% 75%, rgba(230, 246, 244, 0.65), transparent 60%),
    linear-gradient(135deg, rgba(255, 255, 255, 0.88), rgba(248, 250, 252, 0.88));
}
:dir(rtl) .hero__right {
  border-left: none;
  border-right: 1px solid var(--border);
}

.heroCard {
  border: 1px solid var(--border);
  border-radius: 18px;
  background: var(--surface2);
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.06);
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.heroCard__icon {
  width: 44px;
  height: 44px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: rgba(230, 246, 244, 0.85);
  border: 1px solid rgba(15, 127, 116, 0.18);
  color: var(--brand);
  flex: 0 0 auto;
}

.heroCard__title {
  font-weight: 950;
  color: var(--text);
  font-size: 13px;
}
.heroCard__sub {
  margin-top: 3px;
  color: var(--muted);
  font-weight: 800;
  font-size: 12px;
  line-height: 1.6;
}

.heroTag {
  margin-top: 10px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(201, 162, 58, 0.10);
  color: var(--gold);
  border: 1px solid rgba(201, 162, 58, 0.18);
  font-weight: 950;
  font-size: 12px;
}

/* ===== Mobile TOC ===== */
.tocMobile {
  position: relative;
  z-index: 1;
  display: none;
  margin-bottom: 12px;
}
@media (max-width: 1000px) {
  .tocMobile {
    display: block;
  }
}

.tocMobile__btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid var(--border);
  background: var(--surface);
  backdrop-filter: blur(8px);
  border-radius: 18px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  padding: 12px;
  text-align: start;
}

.tocMobile__left {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.tocMobile__dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: rgba(15, 127, 116, 0.55);
}
.tocMobile__label {
  color: var(--text);
  font-weight: 950;
  font-size: 13px;
}

.tocMobile__active {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--muted);
  font-weight: 900;
  font-size: 12px;
  max-width: 48%;
}
.tocMobile__panel {
  margin-top: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  backdrop-filter: blur(8px);
  border-radius: 18px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  padding: 8px;
}

.tocItem {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 10px;
  border-radius: 14px;
  background: transparent;
  border: 1px solid transparent;
  color: var(--muted);
  font-weight: 900;
  font-size: 12px;
  text-align: start;
  transition: 0.15s ease;
}
.tocItem:hover {
  background: var(--brandSoft);
  border-color: rgba(15, 127, 116, 0.14);
  color: var(--text);
}
.tocItem--active {
  background: rgba(15, 127, 116, 0.12);
  border-color: rgba(15, 127, 116, 0.18);
  color: var(--text);
}

/* ===== Layout ===== */
.layout {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 12px;
}
@media (max-width: 1000px) {
  .layout {
    grid-template-columns: 1fr;
  }
}

/* ===== Desktop sidebar ===== */
.side {
  position: sticky;
  top: 14px;
  height: fit-content;
}
@media (max-width: 1000px) {
  .side {
    display: none;
  }
}

.sideCard {
  border: 1px solid var(--border);
  background: var(--surface);
  backdrop-filter: blur(8px);
  border-radius: 20px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  padding: 12px;
}

.sideCard__head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 950;
  color: var(--text);
  font-size: 13px;
  margin-bottom: 10px;
}

.sideCard__list {
  display: grid;
  gap: 6px;
}

.sideLink {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: start;
  padding: 10px 10px;
  border-radius: 14px;
  border: 1px solid transparent;
  color: var(--muted);
  font-weight: 900;
  font-size: 12px;
  background: transparent;
  transition: 0.15s ease;
}
.sideLink:hover {
  background: var(--brandSoft);
  border-color: rgba(15, 127, 116, 0.14);
  color: var(--text);
}
.sideLink__dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: rgba(15, 127, 116, 0.50);
  flex: 0 0 auto;
}
.sideLink--active {
  background: rgba(15, 127, 116, 0.12);
  border-color: rgba(15, 127, 116, 0.18);
  color: var(--text);
}

/* ===== Main cards ===== */
.main {
  display: grid;
  gap: 12px;
}

.card {
  border: 1px solid var(--border);
  background: var(--surface);
  backdrop-filter: blur(8px);
  border-radius: 22px;
  box-shadow: var(--shadow);
  padding: 14px;
  scroll-margin-top: 90px;
  transition: 0.18s ease;
}
.card--active {
  border-color: rgba(15, 127, 116, 0.22);
  box-shadow: 0 18px 45px rgba(15, 127, 116, 0.10);
}

.card__head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.iconBox {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(15, 23, 42, 0.08);
  flex: 0 0 auto;
}

.card__title {
  margin: 0;
  color: var(--text);
  font-weight: 950;
  font-size: 14px;
}
.card__hint {
  margin: 4px 0 0;
  color: rgba(100, 116, 139, 0.92);
  font-weight: 800;
  font-size: 12px;
}

.card__text {
  margin: 10px 0 0;
  color: var(--muted);
  font-weight: 800;
  font-size: 13px;
  line-height: 1.9;
}

.card__list {
  margin: 10px 0 0;
  padding-inline-start: 18px;
  color: var(--muted);
  font-weight: 800;
  font-size: 13px;
  line-height: 1.9;
}
.card__list li {
  margin: 6px 0;
}

/* Card footer mini actions */
.card__footer {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.miniBtn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 12px;
  border-radius: 14px;
  background: rgba(15, 127, 116, 0.10);
  color: var(--brand);
  border: 1px solid rgba(15, 127, 116, 0.18);
  font-weight: 950;
  font-size: 12px;
  transition: 0.15s ease;
}
.miniBtn:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 28px rgba(15, 127, 116, 0.14);
}
.miniBtn--ghost {
  background: rgba(255, 255, 255, 0.75);
  color: var(--text);
  border: 1px solid var(--border);
}
.miniBtn--ghost:hover {
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.08);
}

/* ===== Tone styles ===== */
.tone--teal {
  background: rgba(230, 246, 244, 0.85);
  color: var(--brand);
  border-color: rgba(15, 127, 116, 0.18);
}
.tone--gold {
  background: rgba(201, 162, 58, 0.10);
  color: var(--gold);
  border-color: rgba(201, 162, 58, 0.18);
}
.tone--primary {
  background: rgba(205, 239, 251, 0.50);
  color: var(--text);
  border-color: rgba(15, 23, 42, 0.08);
}

/* ===== Note / CTA ===== */
.note {
  position: relative;
  z-index: 1;
  margin-top: 12px;
  border: 1px solid var(--border);
  background: var(--surface);
  backdrop-filter: blur(8px);
  border-radius: 22px;
  box-shadow: var(--shadow);
  padding: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.note__left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.note__icon {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: rgba(205, 239, 251, 0.45);
  border: 1px solid rgba(15, 23, 42, 0.08);
  color: var(--text);
  flex: 0 0 auto;
}
.note__title {
  font-weight: 950;
  color: var(--text);
  font-size: 13px;
}
.note__desc {
  margin-top: 2px;
  color: var(--muted);
  font-weight: 800;
  font-size: 12px;
  line-height: 1.7;
}
</style>